import React from 'react';
import { useParams } from 'react-router';
import { Spin } from 'antd';
import { ClaimDetailsContext } from '../ClaimOverviewShell/ClaimDetailsContext';
import ClaimFileViewerContainer from '../ClaimOverviewShell/SourceViewerContainer/ClaimFileViewerContainer/ClaimFileViewerContainer';
import useFetchFilesHook from '../ClaimOverviewShell/SourceViewerContainer/ClaimFileViewerContainer/useFetchFilesHook';
import styles from './FileViewerPage.module.scss';

interface Props {
  fileId?: string;
  page?: number;
}

// Module-level in-flight set dedupes dispatches across component instances.
// The "+ Workspace" promotion briefly co-mounts EscFileViewerPage in the
// drawer and in the new workspace before the drawer closes; per-instance
// refs aren't enough — both instances would dispatch since `fileReady` is
// still false. Cleared once the file lands in the slice cache.
const inFlightFileIds = new Set<string>();

const EscFileViewerPage: React.FC<Props> = ({ fileId: fileIdProp, page }) => {
  const { dossierId, fileId: fileIdParam } = useParams<{
    dossierId: string;
    fileId?: string;
  }>();
  const fileId = fileIdProp ?? fileIdParam;
  const [{ files }, filesHandler] = useFetchFilesHook(dossierId ?? '');
  const fileReady = fileId ? !!files[fileId] : false;

  React.useEffect(() => {
    if (!fileId || fileReady || inFlightFileIds.has(fileId)) {
      return;
    }
    inFlightFileIds.add(fileId);
    filesHandler.fetchFile({ fileId, stateDb: true });
  }, [fileId, fileReady, filesHandler]);

  React.useEffect(() => {
    if (fileReady && fileId) {
      inFlightFileIds.delete(fileId);
    }
  }, [fileReady, fileId]);

  if (!dossierId || !fileId) {
    return <div className={styles.loading}>File not found</div>;
  }

  return (
    <ClaimDetailsContext.Provider value={{ files }}>
      <div className={styles.container}>
        {fileReady ? (
          <ClaimFileViewerContainer
            dossierId={dossierId}
            fileId={fileId}
            defaultPage={page}
          />
        ) : (
          <div className={styles.loading}>
            <Spin />
          </div>
        )}
      </div>
    </ClaimDetailsContext.Provider>
  );
};

export default EscFileViewerPage;
